import classNames from "classnames";
import { USD_CURRENCY } from "constants.js";
import { Field } from "formik";
import moment from "moment";
import * as FormFieldsComponents from "pages/forms/_components/_fields/Index";
import Box from "pages/_components/Box";
import PageLoading from "pages/_components/PageLoading";
import { resizableRoute } from "pages/_components/Resizable";
import Row from "pages/_components/Row";
import { arrayOf, bool, func, shape, string } from "prop-types";
import React, { useEffect, useState } from "react";
import Col from "react-bootstrap/lib/Col";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { actions as formActions, selectors as formSelectors } from "reducers/form";
import { actions as creditCardActions, selectors as creditCardSelectors } from "reducers/creditCard";
import { selectors as i18nSelectors } from "reducers/i18n";
import { selectors as templateSelectors } from "reducers/template";
import { compose } from "redux";
import * as i18n from "util/i18n";
import * as schedulerUtils from "util/scheduler";
import { validationAccount, validationAmount } from "util/validationSchemaUtil";
import * as yup from "yup";
import * as creditCardsUtil from "util/creditCards";
import useAsyncTicketProcessing from "hooks/useAsyncTicketProcessing";
import FormTransition from "../_components/FormTransition";
import { SCHEDULER_DATE } from "../_components/_fields/ReadText";
import {
    useCreditCardPayment,
    useSelectorOptions,
    useCreditCardRechargeSelectorOptions,
} from "./hooks/PayCreditCardHooks";
import { useDataAmount, useGenericProps, useMetaData } from "./hooks/TransferInternalHooks";
import { PDFAmountField, PDFAmountFieldError, PDFProductSelectorField, PDFTextField } from "./PDFTicket";

const ID_FORM = "creditCard.recharge.creditCardLocal";
const ID_ACTIVITY = `${ID_FORM}.send`;
const ID_ACTIVITY_PRE = `${ID_FORM}.pre`;
const TITLE_FORM = "forms.creditCard.recharge.formName";

const RechargeCreditCardLocal = (props) => {
    const {
        preDataForm,
        location,
        isDesktop,
        mode,
        dispatch,
        transaction,
        currentLang,
        creditCardList,
        fetchingList,
        usesJointAccount,
    } = props;
    const [TooltipProcessingComponent] = useAsyncTicketProcessing(transaction);

    const [metadata] = useMetaData(preDataForm, ID_ACTIVITY);
    const [genericProps] = useGenericProps(props, ID_ACTIVITY);
    let accountOptions = useSelectorOptions(preDataForm);
    let creditCardOptions = useCreditCardRechargeSelectorOptions(creditCardList);
    if (usesJointAccount) {
        const ccData = transaction.data.creditCardData;
        if (ccData) {
            creditCardOptions = { options: [{ ...ccData, id: ccData.idProduct, label: ccData.otherLabel }] };
        }
        const daData = transaction.data.debitAccountData;
        if (daData) {
            accountOptions = { options: [{ ...daData, id: daData.idProduct, label: daData.otherLabel }] };
        }
    }
    const [creditCardPayment] = useCreditCardPayment(location);
    const [dataAmount] = useDataAmount(preDataForm);
    const [amountDebitAccount, setAmountDebitAccount] = useState({ currency: "", quantity: 0 });

    const titleForm = TITLE_FORM;

    useEffect(() => {
        if (mode === "edit") {
            dispatch(creditCardActions.listRequest());

            const formData = { debitAccount: null };
            dispatch(formActions.preForm({ idActivity: ID_ACTIVITY_PRE, formData }));
        }
    }, [dispatch, mode, location]);

    const validationSchema = () =>
        yup.object().shape({
            amount: validationAmount(`forms.${ID_FORM}.amount.required`),
            debitAccount: validationAccount(`forms.${ID_FORM}.debitAccount.invalid`, [
                ...(preDataForm?.debitAccountList || []),
            ]),
            creditCard: validationAccount(
                `forms.${ID_FORM}.creditCard.invalid`,
                creditCardsUtil.getPrepaidCreditCards(creditCardList),
            ),
        });

    const renderTicket = (values) => {
        const vaucherLabel = transaction?.idTransactionStatus === "REJECTED" ? "Reference" : "";
        const showReference =
            transaction?.idTransactionStatus === "FINISHED" || transaction?.idTransactionStatus === "REJECTED";

        return (
            <>
                {transaction?.idTransactionStatus === "FINISHED" ? (
                    <PDFAmountField
                        idForm={ID_FORM}
                        name="amount"
                        values={values}
                        label={i18n.get(`forms.${ID_FORM}.amount.label_view`)}
                    />
                ) : (
                    <PDFAmountFieldError
                        idForm={ID_FORM}
                        name="amount"
                        values={values}
                        label={i18n.get(`forms.${ID_FORM}.amount.label_view`)}
                    />
                )}

                {showReference && (
                    <PDFTextField
                        idForm={ID_FORM}
                        name="backendReference"
                        values={values}
                        label={i18n.get(`forms.creditCard.recharge.voucher${vaucherLabel}.label`)}
                    />
                )}

                <PDFTextField
                    idForm={ID_FORM}
                    value={
                        transaction?.idParentTransaction
                            ? moment(transaction?.submitDateTime).format("DD/MM/YYYY")
                            : moment(transaction?.creationDateTime).format("DD/MM/YYYY")
                    }
                    label={i18n.get("forms.creditCard.recharge.date.label_preview")}
                />

                <PDFTextField
                    idForm={ID_FORM}
                    value={`${
                        transaction?.programed ? i18n.get("forms.transaction.ticket.typeOperation.program") : ""
                    }${i18n.get("forms.creditCard.recharge.typeOperation")}`}
                    label={i18n.get("forms.creditCard.recharge.typeOperation.label")}
                />

                <PDFProductSelectorField idForm={ID_FORM} name="debitAccount" values={values} />
                <PDFProductSelectorField idForm={ID_FORM} name="creditCard" values={values} />
            </>
        );
    };

    const renderFields = (setFieldValue, values, setValues, renderScheduler) => {
        if (
            mode !== "view" &&
            (creditCardOptions.length === 0 || accountOptions.length === 0) &&
            (!preDataForm ||
                !preDataForm.currencyList ||
                !preDataForm.debitAccountList ||
                fetchingList ||
                creditCardList.length === 0)
        ) {
            return <PageLoading loading classicStyle={false} />;
        }
        if (values?.debitAccount && mode === "edit") {
            const debitAccount = preDataForm?.debitAccountList?.find((acc) => acc.idProduct === values?.debitAccount);
            const currency = debitAccount?.currency;
            const balance = debitAccount?.balance;

            if (amountDebitAccount?.currency !== currency || amountDebitAccount?.quantity !== balance) {
                setAmountDebitAccount({ currency: debitAccount?.currency, quantity: debitAccount?.balance });
            }
        }
        return (
            <>
                {mode === "edit" && (
                    <>
                        <Row
                            className="mb-lg-7 mb-3"
                            {...(isDesktop && { gapX: "7" })}
                            {...(!isDesktop && { gapY: "3" })}>
                            <Col xs={12} md={6}>
                                <Box
                                    background="white"
                                    borderRadius="default"
                                    className="pl-5 pl-lg-10 pr-5 pr-lg-10 pb-8 pb-md-10 mx-n-5">
                                    <Field
                                        {...genericProps}
                                        component={FormFieldsComponents.ProductselectorCustom}
                                        data={accountOptions}
                                        key="debitAccount"
                                        name="debitAccount"
                                        renderAs="combo"
                                        value={values?.debitAccount}
                                        loadBalance
                                        idField="debitAccount"
                                    />
                                </Box>
                            </Col>
                            <Col xs={12} md={6}>
                                <Box
                                    background="white"
                                    borderRadius="default"
                                    className="pl-5 pl-lg-10 pr-5 pr-lg-10 pb-8 pb-md-10 mx-n-5">
                                    <Field
                                        {...genericProps}
                                        component={FormFieldsComponents.ProductselectorCustom}
                                        data={creditCardOptions}
                                        key="creditCard"
                                        name="creditCard"
                                        renderAs="combo"
                                        value={creditCardPayment || values?.creditCard}
                                        idField="creditCard"
                                    />
                                </Box>
                            </Col>
                        </Row>
                        <Row className="mb-7" {...(isDesktop && { gapX: "7" })} {...(!isDesktop && { gapY: "3" })}>
                            <Col xs={12} md={6}>
                                <Box
                                    {...(isDesktop && { display: "flex", alignX: "center", column: true })}
                                    background="white"
                                    borderRadius="default"
                                    className={classNames({
                                        "min-height-8rem": isDesktop,
                                    })}
                                    fullWidth>
                                    {renderScheduler()}
                                </Box>
                            </Col>
                            <Col xs={12} md={6}>
                                <Box
                                    background="white"
                                    borderRadius="default"
                                    className="pl-5 pl-lg-10 pr-5 pr-lg-10 pt-7 pb-5 pb-md-9 mx-n-5">
                                    <Field
                                        {...genericProps}
                                        component={FormFieldsComponents.Amount}
                                        data={dataAmount}
                                        key="amount"
                                        name="amount"
                                        value={{ quantity: values.amount?.quantity, currency: USD_CURRENCY }}
                                        idField="amount"
                                        customPlaceholderCurrency=""
                                        customPlaceholderQuantity="0.00"
                                        quantityTextAlign="text-left"
                                        disabledCurrency
                                    />
                                </Box>
                            </Col>
                        </Row>
                    </>
                )}
                {mode === "preview" || mode === "view" ? (
                    <>
                        <Box display="flex" alignX="center" fullWidth className="amount-wrapper">
                            <Field
                                {...genericProps}
                                component={FormFieldsComponents.Amount}
                                data={dataAmount}
                                key="amount"
                                name="amount"
                                value={values.amount}
                                idField="amount"
                                bigDataAmount
                                label_viewMap={
                                    transaction?.idTransactionStatus === "FINISHED"
                                        ? { [currentLang]: i18n.get(`forms.${ID_FORM}.amount.label_view`) }
                                        : { [currentLang]: i18n.get(`forms.${ID_FORM}.amount.label`) }
                                }
                            />
                        </Box>

                        {TooltipProcessingComponent}

                        <Box className={classNames("mt-3", { "mt-9 mx-7": mode === "view" })}>
                            <FormFieldsComponents.ReadTextCustom
                                {...genericProps}
                                value={values?.backendReference || transaction?.data?.backendReference || ""}
                                label="forms.creditCard.recharge.voucher.label"
                                shouldRender={
                                    mode === "view" &&
                                    !!(values?.backendReference || transaction?.data?.backendReference)
                                }
                            />
                            {values && values.backendReference && (
                                <FormFieldsComponents.ReadTextCustom
                                    {...genericProps}
                                    value={values?.backendReference}
                                    label="forms.creditCard.recharge.voucherReference.label"
                                    shouldRender={
                                        mode === "view" &&
                                        transaction &&
                                        (transaction?.idTransactionStatus === "PENDING" ||
                                            transaction?.idTransactionStatus === "PROCESSING" ||
                                            transaction?.idTransactionStatus === "REJECTED")
                                    }
                                />
                            )}
                            <FormFieldsComponents.ReadTextCustom
                                {...genericProps}
                                value={
                                    // eslint-disable-next-line no-nested-ternary
                                    mode === "preview"
                                        ? moment().format("DD/MM/YYYY")
                                        : transaction?.idParentTransaction
                                        ? moment(transaction?.submitDateTime).format("DD/MM/YYYY")
                                        : moment(transaction?.creationDateTime).format("DD/MM/YYYY")
                                }
                                label="forms.creditCard.recharge.date.label_preview"
                            />
                            <FormFieldsComponents.ReadTextCustom
                                {...genericProps}
                                value={`${
                                    transaction?.programed
                                        ? i18n.get("forms.transaction.ticket.typeOperation.program")
                                        : ""
                                }${i18n.get("forms.creditCard.recharge.typeOperation")}`}
                                label="forms.creditCard.recharge.typeOperation.label"
                                shouldRender={mode === "view"}
                            />

                            <Field
                                {...genericProps}
                                component={FormFieldsComponents.ProductselectorCustom}
                                data={accountOptions}
                                key="debitAccount"
                                name="debitAccount"
                                renderAs="combo"
                                value={values?.debitAccount}
                                idField="debitAccount"
                            />
                            <Field
                                {...genericProps}
                                component={FormFieldsComponents.ProductselectorCustom}
                                data={creditCardOptions}
                                key="creditCard"
                                name="creditCard"
                                renderAs="combo"
                                value={creditCardPayment || values?.creditCard}
                                idField="creditCard"
                            />

                            {values?.scheduler?.selectedOption &&
                            values?.scheduler?.selectedOption === schedulerUtils.TODAY &&
                            mode === "preview" ? (
                                <FormFieldsComponents.ReadTextCustom
                                    {...genericProps}
                                    value={i18n.get("scheduler.immediate.radiobutton.label.cardPayment")}
                                    label="forms.creditCard.recharge.makepay.label_preview"
                                />
                            ) : null}

                            {values?.scheduler?.selectedOption &&
                            values?.scheduler?.selectedOption !== schedulerUtils.TODAY ? (
                                <FormFieldsComponents.ReadTextCustom
                                    {...genericProps}
                                    type={SCHEDULER_DATE}
                                    value={values?.scheduler}
                                    label="forms.creditCard.recharge.dateScheduler.label_preview"
                                />
                            ) : null}
                        </Box>
                    </>
                ) : null}
            </>
        );
    };

    const formProps = {
        title: titleForm,
        metadata,
        renderFields,
        renderTicket,
        useDefaultSubmit: true,
        preData: preDataForm,
        isCustom: true,
        idActivity: ID_ACTIVITY,
        titleConfirmation: true,
        titleFormConfirmation: "OTP CODE",
        validationSchema,
        showSchedulerMessage: false,
        invalidateCache: true,
    };
    return <FormTransition {...props} {...formProps} />;
};

RechargeCreditCardLocal.propTypes = {
    preDataForm: shape({}),
    location: shape({}),
    isDesktop: bool,
    dispatch: func,
    mode: string,
    transaction: shape({}),
    currentLang: string,
    fetchingList: bool,
    creditCardList: arrayOf(shape({})),
    usesJointAccount: bool,
};
RechargeCreditCardLocal.defaultProps = {
    dispatch: () => {},
    mode: "",
    preDataForm: {},
    location: {},
    isDesktop: false,
    transaction: null,
    currentLang: "",
    fetchingList: false,
    creditCardList: [],
    usesJointAccount: false,
};
const mapStateToProps = (state) => ({
    id: formSelectors.getId(state),
    fetching: false,
    currentLang: i18nSelectors.getLang(state),
    data: formSelectors.getData(state),
    transaction: formSelectors.getTransaction(state),
    childrenTransactions: formSelectors.getChildrenTransactions(state),
    parentTransaction: formSelectors.getParentTransaction(state),
    ticketConfirmation: true,
    templates: templateSelectors.getTemplateList(state),
    mode: formSelectors.getMode(state),
    credentialsGroups: formSelectors.getCredentialsGroups(state),
    isCancellingTransaction: formSelectors.getIsCancellingTransaction(state),
    preDataForm: formSelectors.getPreData(state),
    previewData: formSelectors.getPreviewData(state),
    postData: formSelectors.getData(state),
    fetchingList: creditCardSelectors.isFetching(state),
    creditCardList: creditCardSelectors.getList(state),
    usesJointAccount: formSelectors.getUsesJointAccount(state),
});

export default compose(connect(mapStateToProps), withRouter)(resizableRoute(RechargeCreditCardLocal));
